import React, { createContext, memo, useContext, type FC, type ReactNode } from 'react';
import { TouchableOpacity, type ViewStyle } from 'react-native';

import { avatarDefault } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Image } from '@/components/ui/image';
import { Text, TextProps } from '@/components/ui/text';
import { createStyles } from '@/hooks';
import { colors } from '@/theme/colors';

export type AvatarSize = 'sm' | 'md' | 'lg';

export type AvatarContextValue = {
  size: number;
  borderColor: string;
  borderWidth: number;
};

const AvatarContext = createContext<AvatarContextValue | null>(null);

const useAvatarContext = () => {
  const ctx = useContext(AvatarContext);
  if (!ctx) {
    throw new Error('Avatar subcomponents must be used within an <Avatar> root component.');
  }
  return ctx;
};

export type AvatarRootProps = {
  children: ReactNode;
  /** Size in px (diameter of the circle). Default 32. */
  size?: number;
  /** Border color of the circle. Defaults to colors['5']. */
  borderColor?: string;
  /** Border width of the circle in px. Defaults to 1. */
  borderWidth?: number;
  style?: ViewStyle | ViewStyle[];
  onPress?: () => void;
  activeOpacity?: number;
};

const AvatarRoot: FC<AvatarRootProps> = ({
  children,
  size = 32,
  borderColor = colors['5'],
  borderWidth = 1,
  style,
  onPress,
  activeOpacity,
}) => {
  const isDisabled = !onPress;

  return (
    <AvatarContext.Provider value={{ size, borderColor, borderWidth }}>
      <TouchableOpacity
        activeOpacity={activeOpacity ?? 0.8}
        onPress={onPress}
        disabled={isDisabled}>
        <Stack align="center" justify="center" style={style}>
          {children}
        </Stack>
      </TouchableOpacity>
    </AvatarContext.Provider>
  );
};

export type AvatarImageProps = {
  /** Remote or local image source. If not provided, only the circle frame is shown. */
  source?: React.ComponentProps<typeof Image>['source'];

  size?: number;

  disableCircular?: boolean;

  /** Background color of the circle frame. Defaults to colors['2']. */
  backgroundColor?: string;
};

const AvatarImage: FC<AvatarImageProps> = ({
  source,
  size: sizeProp,
  disableCircular,
  backgroundColor = colors['2'],
}) => {
  const { size: _size, borderColor, borderWidth } = useAvatarContext();
  const diameter = _size;

  return (
    <Stack
      width={diameter}
      height={diameter}
      align="center"
      justify="center"
      borderRadius={diameter / 2}
      borderWidth={borderWidth}
      borderColor={borderColor}
      backgroundColor={backgroundColor}
      overflow="hidden">
      {source ? (
        <Image
          source={source}
          width={sizeProp ?? diameter}
          height={sizeProp ?? diameter}
          circular={!disableCircular}
          cachePolicy="immutable"
          persistLoadedUri
          resizeMode="cover"
          defaultSource={avatarDefault}
        />
      ) : null}
    </Stack>
  );
};

export type AvatarLabelProps = TextProps;

const AvatarLabel: FC<AvatarLabelProps> = ({ children, ...props }) => {
  const styles = useStyles();
  const { size } = useAvatarContext();

  if (children == null) return null;

  return (
    <Text style={[styles.label, { width: size }]} numberOfLines={1} ellipsizeMode="clip" {...props}>
      {children}
    </Text>
  );
};

const useStyles = createStyles(({ scale }) => ({
  root: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    marginTop: scale(4),
    color: colors['10'],
    textAlign: 'center',
  },
}));

export const Avatar = Object.assign(memo(AvatarRoot), {
  Image: memo(AvatarImage),
  Label: memo(AvatarLabel),
});

export default Avatar;
